import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  CreateSearchBacklogItemRequest,
  CreateSearchCoworkerRequest,
  CreateSearchCycleRequest,
  CreateSearchTaskRequest,
  SearchBacklogItemDto,
  SearchChecklistRunDto,
  SearchChecklistTemplateDto,
  SearchCoworkerDto,
  SearchCoworkerPlayDto,
  SearchCoworkerRunDto,
  SearchCoworkerScheduleDto,
  SearchCycleDto,
  SearchDecayItemDto,
  CreateAceBriefFromClusterResultDto,
  SearchKeywordClusterDetailDto,
  SearchKeywordClusterListItemDto,
  SearchKeywordImportResultDto,
  SearchKeywordListItemDto,
  SearchKeywordUrlMapDto,
  SearchPositionCatalogItemDto,
  SearchPropertyDetailDto,
  SearchPropertyGoalDto,
  SearchPropertyListItemDto,
  SearchRecommendationDto,
  SearchRedirectDto,
  SearchRedirectImportResultDto,
  SearchTaskDetailDto,
  SearchTaskListItemDto,
  SearchTeamSeatDto,
  SearchUrlInventoryDto,
  SearchWorkHomeDto,
  SearchWorklogDto,
  UpdateSearchCoworkerRequest,
  UpsertSearchCoworkerScheduleRequest,
  UpsertSearchKeywordClusterRequest,
  UpsertSearchKeywordRequest,
  UpsertSearchKeywordUrlMapRequest,
  UpsertSearchPropertyGoalRequest,
  UpsertSearchPropertyRequest,
  UpsertSearchTeamSeatRequest,
  UpsertSearchUrlInventoryRequest,
  SearchApplyJobDto,
  SearchChangeLogDto,
  SearchCrawlRunDto,
  SearchExternalAdapterConfigureResultDto,
  SearchIssueDto,
  SearchOnpageQaResultDto,
  SearchLinkProspectDto,
  SearchOutreachDto,
  SearchOutreachFunnelBucketDto,
  SearchBacklinkDto,
  SearchBacklinkImportResultDto,
  SearchDisavowItemDto,
  SearchLocalProfileDto,
  SearchCitationDto,
  SearchReviewTaskDto,
  SearchSemAccountOptionDto,
  SearchSemCampaignLinkOptionDto,
  SearchSemWorkbookDto,
  SearchNegativeKeywordDto,
  SearchOpsWebhookDeliveryListItem,
  SearchNegativeExportDto,
  SearchAdsBriefDto,
  SearchSearchTermImportResultDto,
  SearchSemBudgetWarningDto,
  SearchScorecardBoardDto,
  SearchScorecardSnapshotDto,
  SearchAnomalyDto,
  SearchClientReportDto,
  SearchAlgoLogEntryDto
} from './growth-search-ops.models';

@Injectable({ providedIn: 'root' })
export class GrowthSearchOpsApi {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/api/v1/growth/search-ops`;

  listPositions(): Observable<SearchPositionCatalogItemDto[]> {
    return this.http.get<SearchPositionCatalogItemDto[]>(`${this.base}/positions`);
  }

  getWorkHome(propertyId?: string | null): Observable<SearchWorkHomeDto> {
    let params = new HttpParams();
    if (propertyId) {
      params = params.set('propertyId', propertyId);
    }
    return this.http.get<SearchWorkHomeDto>(`${this.base}/work-home`, { params });
  }

  listProperties(): Observable<SearchPropertyListItemDto[]> {
    return this.http.get<SearchPropertyListItemDto[]>(`${this.base}/properties`);
  }

  getProperty(id: string): Observable<SearchPropertyDetailDto> {
    return this.http.get<SearchPropertyDetailDto>(`${this.base}/properties/${id}`);
  }

  createProperty(payload: UpsertSearchPropertyRequest): Observable<SearchPropertyDetailDto> {
    return this.http.post<SearchPropertyDetailDto>(`${this.base}/properties`, payload);
  }

  updateProperty(id: string, payload: UpsertSearchPropertyRequest): Observable<SearchPropertyDetailDto> {
    return this.http.put<SearchPropertyDetailDto>(`${this.base}/properties/${id}`, payload);
  }

  configureExternalAdapter(
    propertyId: string,
    payload: { endpointUrl: string; ackTimeoutMinutes?: number | null; rotateApiKey?: boolean }
  ): Observable<SearchExternalAdapterConfigureResultDto> {
    return this.http.put<SearchExternalAdapterConfigureResultDto>(`${this.base}/properties/${propertyId}/adapter`, payload);
  }

  listWebhookDeliveries(propertyId: string): Observable<SearchOpsWebhookDeliveryListItem[]> {
    return this.http.get<SearchOpsWebhookDeliveryListItem[]>(`${this.base}/properties/${propertyId}/webhook-deliveries`);
  }

  listSeats(propertyId: string): Observable<SearchTeamSeatDto[]> {
    return this.http.get<SearchTeamSeatDto[]>(`${this.base}/properties/${propertyId}/seats`);
  }

  createSeat(propertyId: string, payload: UpsertSearchTeamSeatRequest): Observable<SearchTeamSeatDto> {
    return this.http.post<SearchTeamSeatDto>(`${this.base}/properties/${propertyId}/seats`, payload);
  }

  updateSeat(propertyId: string, seatId: string, payload: UpsertSearchTeamSeatRequest): Observable<SearchTeamSeatDto> {
    return this.http.put<SearchTeamSeatDto>(`${this.base}/properties/${propertyId}/seats/${seatId}`, payload);
  }

  listGoals(propertyId: string): Observable<SearchPropertyGoalDto[]> {
    return this.http.get<SearchPropertyGoalDto[]>(`${this.base}/properties/${propertyId}/goals`);
  }

  createGoal(propertyId: string, payload: UpsertSearchPropertyGoalRequest): Observable<SearchPropertyGoalDto> {
    return this.http.post<SearchPropertyGoalDto>(`${this.base}/properties/${propertyId}/goals`, payload);
  }

  updateGoal(propertyId: string, goalId: string, payload: UpsertSearchPropertyGoalRequest): Observable<SearchPropertyGoalDto> {
    return this.http.put<SearchPropertyGoalDto>(`${this.base}/properties/${propertyId}/goals/${goalId}`, payload);
  }

  listChecklists(): Observable<SearchChecklistTemplateDto[]> {
    return this.http.get<SearchChecklistTemplateDto[]>(`${this.base}/checklists`);
  }

  listCycles(propertyId: string): Observable<SearchCycleDto[]> {
    return this.http.get<SearchCycleDto[]>(`${this.base}/properties/${propertyId}/cycles`);
  }

  createCycle(propertyId: string, payload: CreateSearchCycleRequest): Observable<SearchCycleDto> {
    return this.http.post<SearchCycleDto>(`${this.base}/properties/${propertyId}/cycles`, payload);
  }

  closeCycle(cycleId: string): Observable<SearchCycleDto> {
    return this.http.post<SearchCycleDto>(`${this.base}/cycles/${cycleId}/close`, {});
  }

  listBacklog(
    propertyId: string,
    filters?: { position?: string | null; status?: string | null; cycleId?: string | null }
  ): Observable<SearchBacklogItemDto[]> {
    let params = new HttpParams();
    if (filters?.position) {
      params = params.set('position', filters.position);
    }
    if (filters?.status) {
      params = params.set('status', filters.status);
    }
    if (filters?.cycleId) {
      params = params.set('cycleId', filters.cycleId);
    }
    return this.http.get<SearchBacklogItemDto[]>(`${this.base}/properties/${propertyId}/backlog`, { params });
  }

  createBacklogItem(propertyId: string, payload: CreateSearchBacklogItemRequest): Observable<SearchBacklogItemDto> {
    return this.http.post<SearchBacklogItemDto>(`${this.base}/properties/${propertyId}/backlog`, payload);
  }

  listTasks(
    propertyId: string,
    filters?: { position?: string | null; status?: string | null; cycleId?: string | null }
  ): Observable<SearchTaskListItemDto[]> {
    let params = new HttpParams();
    if (filters?.position) {
      params = params.set('position', filters.position);
    }
    if (filters?.status) {
      params = params.set('status', filters.status);
    }
    if (filters?.cycleId) {
      params = params.set('cycleId', filters.cycleId);
    }
    return this.http.get<SearchTaskListItemDto[]>(`${this.base}/properties/${propertyId}/tasks`, { params });
  }

  createTask(propertyId: string, payload: CreateSearchTaskRequest): Observable<SearchTaskListItemDto> {
    return this.http.post<SearchTaskListItemDto>(`${this.base}/properties/${propertyId}/tasks`, payload);
  }

  getTask(taskId: string): Observable<SearchTaskDetailDto> {
    return this.http.get<SearchTaskDetailDto>(`${this.base}/tasks/${taskId}`);
  }

  transitionTask(taskId: string, toStatus: string): Observable<SearchTaskListItemDto> {
    return this.http.post<SearchTaskListItemDto>(`${this.base}/tasks/${taskId}/transition`, { toStatus });
  }

  upsertTaskChecklist(taskId: string, templateKey: string, resultsJson: string): Observable<SearchChecklistRunDto> {
    return this.http.put<SearchChecklistRunDto>(`${this.base}/tasks/${taskId}/checklist`, { templateKey, resultsJson });
  }

  addWorklog(taskId: string, minutes: number, note?: string | null): Observable<SearchWorklogDto> {
    return this.http.post<SearchWorklogDto>(`${this.base}/tasks/${taskId}/worklog`, { minutes, note });
  }

  listRecommendations(propertyId: string, status?: string | null): Observable<SearchRecommendationDto[]> {
    let params = new HttpParams();
    if (status) {
      params = params.set('status', status);
    }
    return this.http.get<SearchRecommendationDto[]>(`${this.base}/properties/${propertyId}/recommendations`, { params });
  }

  acceptRecommendation(id: string): Observable<void> {
    return this.http.post<void>(`${this.base}/recommendations/${id}/accept`, {});
  }

  rejectRecommendation(id: string): Observable<void> {
    return this.http.post<void>(`${this.base}/recommendations/${id}/reject`, {});
  }

  listCoworkers(propertyId: string): Observable<SearchCoworkerDto[]> {
    return this.http.get<SearchCoworkerDto[]>(`${this.base}/coworkers`, {
      params: new HttpParams().set('propertyId', propertyId)
    });
  }

  createCoworker(payload: CreateSearchCoworkerRequest): Observable<SearchCoworkerDto> {
    return this.http.post<SearchCoworkerDto>(`${this.base}/coworkers`, payload);
  }

  getCoworker(id: string): Observable<SearchCoworkerDto> {
    return this.http.get<SearchCoworkerDto>(`${this.base}/coworkers/${id}`);
  }

  updateCoworker(id: string, payload: UpdateSearchCoworkerRequest): Observable<SearchCoworkerDto> {
    return this.http.put<SearchCoworkerDto>(`${this.base}/coworkers/${id}`, payload);
  }

  pauseCoworker(id: string): Observable<void> {
    return this.http.post<void>(`${this.base}/coworkers/${id}/pause`, {});
  }

  resumeCoworker(id: string): Observable<void> {
    return this.http.post<void>(`${this.base}/coworkers/${id}/resume`, {});
  }

  upsertPlays(id: string, playKey: string, enabled: boolean): Observable<SearchCoworkerPlayDto[]> {
    return this.http.put<SearchCoworkerPlayDto[]>(`${this.base}/coworkers/${id}/plays`, {
      items: [{ playKey, enabled, configJson: '{}' }]
    });
  }

  listSchedules(id: string): Observable<SearchCoworkerScheduleDto[]> {
    return this.http.get<SearchCoworkerScheduleDto[]>(`${this.base}/coworkers/${id}/schedules`);
  }

  upsertSchedule(id: string, payload: UpsertSearchCoworkerScheduleRequest): Observable<SearchCoworkerScheduleDto> {
    return this.http.put<SearchCoworkerScheduleDto>(`${this.base}/coworkers/${id}/schedules`, payload);
  }

  runNow(id: string): Observable<SearchCoworkerRunDto> {
    return this.http.post<SearchCoworkerRunDto>(`${this.base}/coworkers/${id}/run-now`, {});
  }

  listRuns(id: string): Observable<SearchCoworkerRunDto[]> {
    return this.http.get<SearchCoworkerRunDto[]>(`${this.base}/coworkers/${id}/runs`);
  }

  listKeywords(propertyId: string, clusterId?: string | null): Observable<SearchKeywordListItemDto[]> {
    let params = new HttpParams();
    if (clusterId) {
      params = params.set('clusterId', clusterId);
    }
    return this.http.get<SearchKeywordListItemDto[]>(`${this.base}/properties/${propertyId}/keywords`, { params });
  }

  upsertKeyword(propertyId: string, payload: UpsertSearchKeywordRequest): Observable<SearchKeywordListItemDto> {
    return this.http.post<SearchKeywordListItemDto>(`${this.base}/properties/${propertyId}/keywords`, payload);
  }

  importKeywords(propertyId: string, csv: string): Observable<SearchKeywordImportResultDto> {
    return this.http.post<SearchKeywordImportResultDto>(`${this.base}/keywords/import`, {
      searchPropertyId: propertyId,
      csv
    });
  }

  listClusters(propertyId: string): Observable<SearchKeywordClusterListItemDto[]> {
    return this.http.get<SearchKeywordClusterListItemDto[]>(`${this.base}/properties/${propertyId}/clusters`);
  }

  createCluster(propertyId: string, payload: UpsertSearchKeywordClusterRequest): Observable<SearchKeywordClusterListItemDto> {
    return this.http.post<SearchKeywordClusterListItemDto>(`${this.base}/properties/${propertyId}/clusters`, payload);
  }

  getCluster(id: string): Observable<SearchKeywordClusterDetailDto> {
    return this.http.get<SearchKeywordClusterDetailDto>(`${this.base}/clusters/${id}`);
  }

  upsertMap(payload: UpsertSearchKeywordUrlMapRequest): Observable<SearchKeywordUrlMapDto> {
    return this.http.put<SearchKeywordUrlMapDto>(`${this.base}/maps`, payload);
  }

  listDecay(propertyId: string): Observable<SearchDecayItemDto[]> {
    return this.http.get<SearchDecayItemDto[]>(`${this.base}/properties/${propertyId}/decay`);
  }

  createAceBrief(clusterId: string): Observable<CreateAceBriefFromClusterResultDto> {
    return this.http.post<CreateAceBriefFromClusterResultDto>(`${this.base}/clusters/${clusterId}/create-ace-brief`, {});
  }

  listUrls(propertyId: string): Observable<SearchUrlInventoryDto[]> {
    return this.http.get<SearchUrlInventoryDto[]>(`${this.base}/properties/${propertyId}/urls`);
  }

  upsertUrl(propertyId: string, payload: UpsertSearchUrlInventoryRequest): Observable<SearchUrlInventoryDto> {
    return this.http.post<SearchUrlInventoryDto>(`${this.base}/properties/${propertyId}/urls`, payload);
  }

  runOnpageQa(
    urlId: string,
    payload: { resultsJson: string; title?: string | null; description?: string | null; createApplyJob: boolean }
  ): Observable<SearchOnpageQaResultDto> {
    return this.http.post<SearchOnpageQaResultDto>(`${this.base}/urls/${urlId}/onpage-qa`, payload);
  }

  listCrawls(propertyId: string): Observable<SearchCrawlRunDto[]> {
    return this.http.get<SearchCrawlRunDto[]>(`${this.base}/properties/${propertyId}/crawls`);
  }

  runCrawl(propertyId: string, provider = 'sandbox'): Observable<SearchCrawlRunDto> {
    return this.http.post<SearchCrawlRunDto>(`${this.base}/properties/${propertyId}/crawls`, { provider });
  }

  listIssues(propertyId: string): Observable<SearchIssueDto[]> {
    return this.http.get<SearchIssueDto[]>(`${this.base}/issues`, { params: { propertyId } });
  }

  createIssue(payload: {
    searchPropertyId: string;
    urlInventoryId?: string | null;
    severity: string;
    category: string;
    evidenceJson?: string | null;
    suggestedTaskType?: string | null;
  }): Observable<SearchIssueDto> {
    return this.http.post<SearchIssueDto>(`${this.base}/issues`, payload);
  }

  createTaskFromIssue(issueId: string): Observable<SearchTaskListItemDto> {
    return this.http.post<SearchTaskListItemDto>(`${this.base}/issues/${issueId}/create-task`, {});
  }

  listRedirects(propertyId: string): Observable<SearchRedirectDto[]> {
    return this.http.get<SearchRedirectDto[]>(`${this.base}/redirects`, { params: { propertyId } });
  }

  upsertRedirect(payload: {
    searchPropertyId: string;
    fromPath: string;
    toUrl: string;
    redirectType?: string | null;
    reason?: string | null;
  }): Observable<SearchRedirectDto> {
    return this.http.post<SearchRedirectDto>(`${this.base}/redirects`, payload);
  }

  importRedirects(propertyId: string, csv: string, dryRun: boolean): Observable<SearchRedirectImportResultDto> {
    return this.http.post<SearchRedirectImportResultDto>(
      `${this.base}/redirects/import`,
      { searchPropertyId: propertyId, csv },
      { params: { dryRun: String(dryRun) } }
    );
  }

  listChangeLogs(propertyId: string): Observable<SearchChangeLogDto[]> {
    return this.http.get<SearchChangeLogDto[]>(`${this.base}/changelog`, { params: { propertyId } });
  }

  listApplyJobs(propertyId: string): Observable<SearchApplyJobDto[]> {
    return this.http.get<SearchApplyJobDto[]>(`${this.base}/apply-jobs`, { params: { propertyId } });
  }

  createApplyJob(payload: {
    searchPropertyId: string;
    urlInventoryId?: string | null;
    patchJson: string;
    idempotencyKey?: string | null;
  }): Observable<SearchApplyJobDto> {
    return this.http.post<SearchApplyJobDto>(`${this.base}/apply-jobs`, payload);
  }

  approveApplyJob(id: string): Observable<SearchApplyJobDto> {
    return this.http.post<SearchApplyJobDto>(`${this.base}/apply-jobs/${id}/approve`, {});
  }

  sendApplyJob(id: string): Observable<SearchApplyJobDto> {
    return this.http.post<SearchApplyJobDto>(`${this.base}/apply-jobs/${id}/send`, {});
  }

  listProspects(propertyId: string): Observable<SearchLinkProspectDto[]> {
    return this.http.get<SearchLinkProspectDto[]>(`${this.base}/prospects`, { params: { propertyId } });
  }

  upsertProspect(payload: {
    searchPropertyId: string;
    domain: string;
    targetUrl?: string | null;
    contactEmail?: string | null;
    contactPhone?: string | null;
    status?: string | null;
    doNotContact: boolean;
    evidenceJson?: string | null;
  }): Observable<SearchLinkProspectDto> {
    return this.http.post<SearchLinkProspectDto>(`${this.base}/prospects`, payload);
  }

  createTaskFromProspect(id: string): Observable<SearchTaskListItemDto> {
    return this.http.post<SearchTaskListItemDto>(`${this.base}/prospects/${id}/create-task`, {});
  }

  listOutreach(propertyId: string): Observable<SearchOutreachDto[]> {
    return this.http.get<SearchOutreachDto[]>(`${this.base}/outreach`, { params: { propertyId } });
  }

  getOutreachFunnel(propertyId: string): Observable<SearchOutreachFunnelBucketDto[]> {
    return this.http.get<SearchOutreachFunnelBucketDto[]>(`${this.base}/outreach/funnel`, { params: { propertyId } });
  }

  upsertOutreach(payload: {
    searchPropertyId: string;
    prospectId: string;
    channel?: string | null;
    templateKey?: string | null;
    lastTouchAtUtc?: string | null;
    result?: string | null;
    producedBacklinkId?: string | null;
    evidenceJson?: string | null;
  }): Observable<SearchOutreachDto> {
    return this.http.post<SearchOutreachDto>(`${this.base}/outreach`, payload);
  }

  listBacklinks(propertyId: string): Observable<SearchBacklinkDto[]> {
    return this.http.get<SearchBacklinkDto[]>(`${this.base}/backlinks`, { params: { propertyId } });
  }

  importBacklinks(propertyId: string, csv: string): Observable<SearchBacklinkImportResultDto> {
    return this.http.post<SearchBacklinkImportResultDto>(`${this.base}/backlinks/import`, {
      searchPropertyId: propertyId,
      csv
    });
  }

  listDisavow(propertyId: string): Observable<SearchDisavowItemDto[]> {
    return this.http.get<SearchDisavowItemDto[]>(`${this.base}/disavow`, { params: { propertyId } });
  }

  createDisavow(payload: {
    searchPropertyId: string;
    entity: string;
    entityKind?: string | null;
    reason: string;
  }): Observable<SearchDisavowItemDto> {
    return this.http.post<SearchDisavowItemDto>(`${this.base}/disavow`, payload);
  }

  submitDisavow(id: string): Observable<SearchDisavowItemDto> {
    return this.http.post<SearchDisavowItemDto>(`${this.base}/disavow/${id}/submit`, {});
  }

  getLocalProfile(propertyId: string): Observable<SearchLocalProfileDto | null> {
    return this.http.get<SearchLocalProfileDto | null>(`${this.base}/local-profiles`, { params: { propertyId } });
  }

  upsertLocalProfile(payload: {
    searchPropertyId: string;
    napJson?: string | null;
    externalGbpId?: string | null;
    status?: string | null;
  }): Observable<SearchLocalProfileDto> {
    return this.http.post<SearchLocalProfileDto>(`${this.base}/local-profiles`, payload);
  }

  listCitations(propertyId: string): Observable<SearchCitationDto[]> {
    return this.http.get<SearchCitationDto[]>(`${this.base}/citations`, { params: { propertyId } });
  }

  upsertCitation(payload: {
    searchPropertyId: string;
    localProfileId?: string | null;
    directory: string;
    url: string;
    napJson?: string | null;
  }): Observable<SearchCitationDto> {
    return this.http.post<SearchCitationDto>(`${this.base}/citations`, payload);
  }

  listReviewTasks(propertyId: string): Observable<SearchReviewTaskDto[]> {
    return this.http.get<SearchReviewTaskDto[]>(`${this.base}/review-tasks`, { params: { propertyId } });
  }

  upsertReviewTask(payload: {
    searchPropertyId: string;
    localProfileId?: string | null;
    externalId: string;
    action?: string | null;
    status?: string | null;
    evidenceJson?: string | null;
  }): Observable<SearchReviewTaskDto> {
    return this.http.post<SearchReviewTaskDto>(`${this.base}/review-tasks`, payload);
  }

  createTaskFromReview(id: string): Observable<SearchTaskListItemDto> {
    return this.http.post<SearchTaskListItemDto>(`${this.base}/review-tasks/${id}/create-task`, {});
  }

  listSemAccounts(): Observable<SearchSemAccountOptionDto[]> {
    return this.http.get<SearchSemAccountOptionDto[]>(`${this.base}/sem/accounts`);
  }

  listSemCampaignLinks(accountId?: string | null): Observable<SearchSemCampaignLinkOptionDto[]> {
    let params = new HttpParams();
    if (accountId) {
      params = params.set('accountId', accountId);
    }
    return this.http.get<SearchSemCampaignLinkOptionDto[]>(`${this.base}/sem/campaign-links`, { params });
  }

  getSemWorkbook(propertyId: string): Observable<SearchSemWorkbookDto | null> {
    return this.http.get<SearchSemWorkbookDto | null>(`${this.base}/sem/workbook`, { params: { propertyId } });
  }

  upsertSemWorkbook(payload: {
    searchPropertyId: string;
    paidMediaAccountId: string;
    notes?: string | null;
  }): Observable<SearchSemWorkbookDto> {
    return this.http.post<SearchSemWorkbookDto>(`${this.base}/sem/workbook`, payload);
  }

  listNegatives(propertyId: string): Observable<SearchNegativeKeywordDto[]> {
    return this.http.get<SearchNegativeKeywordDto[]>(`${this.base}/sem/negatives`, { params: { propertyId } });
  }

  upsertNegative(payload: {
    searchPropertyId: string;
    phrase: string;
    matchType?: string | null;
    scope?: string | null;
    campaignLinkId?: string | null;
  }): Observable<SearchNegativeKeywordDto> {
    return this.http.post<SearchNegativeKeywordDto>(`${this.base}/sem/negatives`, payload);
  }

  exportNegatives(propertyId: string): Observable<SearchNegativeExportDto> {
    return this.http.get<SearchNegativeExportDto>(`${this.base}/sem/negatives-export`, { params: { propertyId } });
  }

  importSearchTerms(propertyId: string, csv: string): Observable<SearchSearchTermImportResultDto> {
    return this.http.post<SearchSearchTermImportResultDto>(`${this.base}/sem/search-terms/import`, {
      searchPropertyId: propertyId,
      csv
    });
  }

  listAdsBriefs(propertyId: string): Observable<SearchAdsBriefDto[]> {
    return this.http.get<SearchAdsBriefDto[]>(`${this.base}/sem/briefs`, { params: { propertyId } });
  }

  upsertAdsBrief(payload: {
    searchPropertyId: string;
    clusterId?: string | null;
    campaignLinkId?: string | null;
    headlineJson?: string | null;
    descriptionJson?: string | null;
    status?: string | null;
  }): Observable<SearchAdsBriefDto> {
    return this.http.post<SearchAdsBriefDto>(`${this.base}/sem/briefs`, payload);
  }

  createTaskFromAdsBrief(id: string): Observable<SearchTaskListItemDto> {
    return this.http.post<SearchTaskListItemDto>(`${this.base}/sem/briefs/${id}/create-task`, {});
  }

  listBudgetWarnings(propertyId: string): Observable<SearchSemBudgetWarningDto[]> {
    return this.http.get<SearchSemBudgetWarningDto[]>(`${this.base}/sem/budget-warnings`, { params: { propertyId } });
  }

  getScorecard(propertyId: string): Observable<SearchScorecardBoardDto> {
    return this.http.get<SearchScorecardBoardDto>(`${this.base}/properties/${propertyId}/scorecard`);
  }

  upsertScorecard(payload: {
    searchPropertyId: string;
    periodStartUtc: string;
    periodEndUtc: string;
    source?: string | null;
    metricsJson?: string | null;
  }): Observable<SearchScorecardSnapshotDto> {
    return this.http.post<SearchScorecardSnapshotDto>(`${this.base}/scorecards`, payload);
  }

  listAnomalies(propertyId: string): Observable<SearchAnomalyDto[]> {
    return this.http.get<SearchAnomalyDto[]>(`${this.base}/properties/${propertyId}/anomalies`);
  }

  createTaskFromAnomaly(id: string): Observable<SearchTaskListItemDto> {
    return this.http.post<SearchTaskListItemDto>(`${this.base}/anomalies/${id}/create-task`, {});
  }

  listClientReports(propertyId: string): Observable<SearchClientReportDto[]> {
    return this.http.get<SearchClientReportDto[]>(`${this.base}/properties/${propertyId}/reports`);
  }

  upsertClientReport(payload: {
    searchPropertyId: string;
    cycleId: string;
    locale?: string | null;
    bodyJson?: string | null;
  }): Observable<SearchClientReportDto> {
    return this.http.post<SearchClientReportDto>(`${this.base}/reports`, payload);
  }

  getClientReport(id: string): Observable<SearchClientReportDto> {
    return this.http.get<SearchClientReportDto>(`${this.base}/reports/${id}`);
  }

  publishClientReport(id: string): Observable<SearchClientReportDto> {
    return this.http.post<SearchClientReportDto>(`${this.base}/reports/${id}/publish`, {});
  }

  listAlgoLog(propertyId: string): Observable<SearchAlgoLogEntryDto[]> {
    return this.http.get<SearchAlgoLogEntryDto[]>(`${this.base}/properties/${propertyId}/algo-log`);
  }

  upsertAlgoLog(payload: {
    searchPropertyId: string;
    engine?: string | null;
    notedOnUtc: string;
    title: string;
    observedEffectJson?: string | null;
  }): Observable<SearchAlgoLogEntryDto> {
    return this.http.post<SearchAlgoLogEntryDto>(`${this.base}/algo-log`, payload);
  }
}
